import React from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  TimeFilterPreset,
  getAllTimeFilterPresets,
  getTimeFilterPresetLabel,
} from '@/utils/timeFilterPresets';

interface TimeFilterPresetProps {
  value: TimeFilterPreset | undefined;
  onChange: (preset: TimeFilterPreset) => void;
  className?: string;
  disabled?: boolean;
  disabledTooltip?: string;
  disableYOY?: boolean; // Hide YOY option if previous year data unavailable
  visiblePresets?: TimeFilterPreset[]; // Optional: specify which presets to show
}

/**
 * Google Finance-style time filter preset component
 * Displays mutually exclusive pill buttons for time range selection
 */
export function TimeFilterPresetSelector({
  value,
  onChange,
  className,
  disabled = false,
  disabledTooltip,
  disableYOY = false,
  visiblePresets,
}: TimeFilterPresetProps) {
  const allPresets = visiblePresets ?? getAllTimeFilterPresets();
  // Filter out YOY preset if it should be hidden
  const presets = disableYOY
    ? allPresets.filter((preset) => preset !== 'yoy')
    : allPresets;
  const selectedPreset = value || 'all'; // Default to 'all' if undefined

  const content = (
    <div className={cn('flex items-center gap-1', className)}>
      {presets.map((preset) => {
        const isSelected = preset === selectedPreset;

        return (
          <div key={preset} className="relative inline-flex items-center">
            <Button
              variant={isSelected ? 'default' : 'outline'}
              size="sm"
              disabled={disabled}
              className={cn(
                'h-7 px-3 text-xs font-medium transition-colors',
                isSelected
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : 'bg-background text-muted-foreground hover:bg-muted hover:text-foreground',
                disabled && 'opacity-50 cursor-not-allowed',
              )}
              onClick={() => !disabled && onChange(preset)}
            >
              {getTimeFilterPresetLabel(preset)}
            </Button>
          </div>
        );
      })}
    </div>
  );

  if (disabled && disabledTooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-block">{content}</div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{disabledTooltip}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return content;
}
